import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { selectCompanyId } from "modules/company/redux/selectors";
import { EVENT_DISCOVERY_QUERY_KEY } from "modules/event/constants";
import { Errors, trackError } from "modules/monitoring";
import {
  selectCustomDomainCompanyID,
  selectEnableAgencyWhiteLabelling,
} from "modules/settings/redux/selectors";
import { useMemo } from "react";
import { getRemoHosts } from "config";
import { getDiscoverableEvents } from "./request";

export const useEventDiscovery = (query?: string) => {
  const companyId = useSelector(selectCompanyId);
  const enableAgencyWhiteLabelling = useSelector(
    selectEnableAgencyWhiteLabelling,
  );
  const customDomainCompanyID = useSelector(selectCustomDomainCompanyID);
  const isHostnameCustomDomain = !getRemoHosts().includes(window.location.host);
  // only show events that belong to the company using this custom domain, if we're on a custom domain
  // and current company id is different from the company id using this custom domain
  const showWhitelabelledCustomDomainEvents = useMemo(
    () =>
      enableAgencyWhiteLabelling &&
      isHostnameCustomDomain &&
      customDomainCompanyID !== companyId,
    [
      companyId,
      customDomainCompanyID,
      enableAgencyWhiteLabelling,
      isHostnameCustomDomain,
    ],
  );

  const { data, status, refetch } = useQuery({
    queryKey: [EVENT_DISCOVERY_QUERY_KEY, query],

    queryFn: async () => {
      const response = await getDiscoverableEvents(
        showWhitelabelledCustomDomainEvents ? customDomainCompanyID : companyId,
        query,
        showWhitelabelledCustomDomainEvents,
      );

      if (response.message) {
        trackError(response.message, {
          label: Errors.EVENT_DISCOVERY,
        });

        throw new Error(response.message);
      }

      return response;
    },
  });

  return {
    ...data,
    refetch,
    status,
  };
};
